/* eslint-disable */
import { Grid, List, ListItem, Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import withWidth from '@material-ui/core/withWidth';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { compose } from 'recompose';
import { fromEvent } from 'rxjs';
import { debounceTime, delay, filter, scan, switchMap, tap } from 'rxjs/operators';
import withServices from '../../Contexts/Services/WithServices.context';
import themeCreateInjectableStyles from '../../Support/Theme/CreateInjectableStyles/ThemeCreateInjectableStyles.support';
import Notification from '../Notification/Notification.component';
import Spinner from '../Spinner/Spinner.component';
import NotificationsListPropTypes from './NotificationsList.component.props';
import notificationsListStyles from './NotificationsList.component.styles';

const NotificationsList = ({ classes, services }) => {
  const scrollableContainerChildRef = useRef();
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useLayoutEffect(() => {
    // we need to get access to outer container that is not part of this component so we can specify/read its height and make our scrollableContainerChildRef element pixel bigger so it will result in scrollbar. This scrollbar when scrolled to the bottom of the outer container will trigger notifications update
    const scrollable = scrollableContainerChildRef.current.parentElement;
    // our notifications list might be inside of different containers, e.g. in Dialog. Dialog resizes itself as much as possible according to content in it and if content fits then scrollbar is not showing. We need scrollbar to be always showing so user can scroll to the bottom and trigger update so we set outer container height and out notification list to be just tad bigger so scrollbar will show. This works well for Dialogs but if this NotificationsList needs to be inside e.g. NavBar at the top (unlikely) this height might need to be made configurable via props passed to this component
    scrollable.style = `height: 80vh; padding: 0px;`;
    scrollableContainerChildRef.current.style = `height: ${scrollable.clientHeight + 1}px;`;

    // make initial call
    setIsLoading(true);
    const firstCall =
      services.notificationService &&
      services.notificationService.getNotificationsObservable(5).subscribe((result) => {
        setIsLoading(false);
        if (Array.isArray(result.notifications)) {
          setNotifications(result.notifications);
        }
      });

    const fromScroll$ = fromEvent(scrollable, `scroll`);

    const scrollingDown$ = fromScroll$.pipe(
      debounceTime(500),
      scan(
        (acc, el) => {
          return [acc[1], el.target.scrollTop];
        },
        [0, 0],
      ),
      filter((el) => el[1] > el[0]),
    );

    const scrollable$ = scrollingDown$.pipe(
      filter((el) => {
        return scrollable.scrollTop + scrollable.clientHeight >= scrollable.scrollHeight - scrollable.scrollHeight * 0.2;
      }),
      scan((acc) => {
        return acc + 10;
      }, 3),
      tap(() => {
        setIsLoading(true);
      }),
      delay(1000), // wait so the spinner can be seen at least as otherwise ajax call is usually too fast. When api is ready this might be removed
      switchMap((el) => services.notificationService.getNotificationsObservable(el)),
      tap(() => setIsLoading(false)),
    );
    const mainCall = scrollable$.subscribe((result) => {
      if (Array.isArray(result.notifications)) {
        setNotifications(result.notifications);
      }
    });

    return () => {
      if (firstCall) {
        firstCall.unsubscribe();
      }
      mainCall.unsubscribe();
    };
  }, []);

  /**
   * map notification item to jsx fragment
   * @param {Object} el notificaion item
   * @returns {React.Component} The component
   */
  const createNotificationItem = (el) => {
    return (
      <ListItem classes={{ root: classes.listItemRoot }} key={el.id}>
        <Notification notification={el} />
      </ListItem>
    );
  };
  const current = notifications.filter((el) => el.isCurrent).map(createNotificationItem);
  const previous = notifications.filter((el) => !el.isCurrent).map(createNotificationItem);
  /**
   * render notification list
   * @param {string} heading notificaion heading
   * @param {Array} list notificaion list
   * @returns {React.Component} The component
   */
  const renderList = (heading, list) => {
    return (
      <React.Fragment>
        {list.length !== 0 ? (
          <Grid className={classes.heading} item xs={12}>
            <Typography variant="overline">{heading}</Typography>
          </Grid>
        ) : null}
        <Grid item xs={12}>
          <List>{list}</List>
        </Grid>
      </React.Fragment>
    );
  };
  return (
    <div ref={scrollableContainerChildRef}>
      <Grid container>
        {renderList(`Current`, current)}
        {renderList(`Previous`, previous)}
        {isLoading && (
          <Grid item xs={12}>
            <Spinner className={classes.spinner} />
          </Grid>
        )}
      </Grid>
    </div>
  );
};

NotificationsList.propTypes = NotificationsListPropTypes;

export default compose(
  withWidth(),
  withServices,
  withStyles(themeCreateInjectableStyles(notificationsListStyles), { withTheme: true }),
)(NotificationsList);
